import {
  DepositAmountType,
  isDepositForfeitureRule,
  isDepositSettings,
  isDepositTreatmentConfigurationMap,
  isDepositTreatmentOverride,
  type IDepositAmount,
  type IDepositSettings,
} from '@principle-theorem/principle-core/interfaces';
import { isObject, TypeGuard } from '@principle-theorem/shared';

export const GENERIC_DEPOSIT_SAVE_ERROR =
  'Unable to save deposit settings. Please review the deposit amounts and percentages.';

const unnamedTreatmentTemplate = 'a Treatment Template';

type ResolveTreatmentTemplateName = (uid: string) => string | undefined;

/**
 * Resolves the most actionable save error for invalid deposit settings so the
 * settings page can direct users to the specific field that needs correction.
 */
export class DepositSettingsSaveError {
  /**
   * Returns the first specific validation message for a pending deposit save,
   * falling back to the generic message when no field-level cause is known.
   */
  static get(
    deposit: Partial<IDepositSettings>,
    resolveTreatmentTemplateName?: ResolveTreatmentTemplateName
  ): string | undefined {
    if (isDepositSettings(deposit)) {
      return;
    }
    return (
      DepositSettingsSaveError._patientDepositAmountError(deposit) ??
      DepositSettingsSaveError._forfeitureRulesError(deposit) ??
      DepositSettingsSaveError._treatmentTemplateOverridesError(
        deposit,
        resolveTreatmentTemplateName
      ) ??
      DepositSettingsSaveError._treatmentConfigurationDepositsError(deposit) ??
      GENERIC_DEPOSIT_SAVE_ERROR
    );
  }

  private static _patientDepositAmountError(
    deposit: Partial<IDepositSettings>
  ): string | undefined {
    const { patientDepositAmountCents } = deposit;
    if (
      patientDepositAmountCents === undefined ||
      TypeGuard.positiveInteger(patientDepositAmountCents)
    ) {
      return;
    }
    return 'Unable to save: the deposit for patients flagged as deposit required must be a dollar amount greater than $0.00.';
  }

  private static _forfeitureRulesError(
    deposit: Partial<IDepositSettings>
  ): string | undefined {
    const forfeitureRules = deposit.forfeitureRules ?? [];
    if (forfeitureRules.every(isDepositForfeitureRule)) {
      return;
    }
    return 'Unable to save: forfeiture percentages must be whole numbers between 1 and 100.';
  }

  private static _treatmentTemplateOverridesError(
    deposit: Partial<IDepositSettings>,
    resolveTreatmentTemplateName?: ResolveTreatmentTemplateName
  ): string | undefined {
    const invalidOverride = Object.entries(
      deposit.treatmentTemplateOverrides ?? {}
    ).find(([, override]) => !isDepositTreatmentOverride(override));
    if (!invalidOverride) {
      return;
    }

    const [uid, override] = invalidOverride;
    const name =
      resolveTreatmentTemplateName?.(uid) ?? unnamedTreatmentTemplate;
    const amount: IDepositAmount | undefined =
      'amount' in override ? override.amount : undefined;
    if (isObject(amount) && amount.type === DepositAmountType.Fixed) {
      return `Unable to save: the deposit amount for "${name}" must be a dollar amount greater than $0.00.`;
    }
    return `Unable to save: the deposit settings for "${name}" are incomplete.`;
  }

  private static _treatmentConfigurationDepositsError(
    deposit: Partial<IDepositSettings>
  ): string | undefined {
    const { treatmentConfigurationDeposits } = deposit;
    if (
      treatmentConfigurationDeposits === undefined ||
      isDepositTreatmentConfigurationMap(treatmentConfigurationDeposits)
    ) {
      return;
    }
    return 'Unable to save: treatment deposit amounts must be dollar amounts greater than $0.00.';
  }
}
